import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { createExpense, getCategories, getCurrencies, getPolicies, uploadReceipt, processOCR, getReceiptImageUrl } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  ArrowLeft,
  CalendarIcon,
  Upload,
  Loader2,
  Sparkles,
  X,
  FileImage,
  FileText,
  Receipt,
  Eye,
  ZoomIn,
  ZoomOut,
  Camera,
  FlipHorizontal,
  RotateCw
} from "lucide-react";
import { cn } from "../lib/utils";

// Barème kilométrique 2024 (Arrêté du 15/02/2024)
// Format : [taux_t1, taux_t2_a, forfait_t2, taux_t3]
const BAREME_2024 = {
  voiture: {
    "3": [0.529, 0.316, 1065, 0.370],
    "4": [0.606, 0.340, 1330, 0.407],
    "5": [0.636, 0.357, 1395, 0.427],
    "6": [0.665, 0.374, 1457, 0.447],
    "7": [0.697, 0.394, 1515, 0.470],
  },
  moto: {
    "2": [0.395, 0.099, 891,  0.248],
    "5": [0.468, 0.082, 1158, 0.275],
    "6": [0.606, 0.079, 1581, 0.343],
  },
  scooter: {
    "0": [0.272, 0.064, 624, 0.168],
  },
};

const CV_LABELS = {
  voiture: { "3": "3 CV et moins", "4": "4 CV", "5": "5 CV", "6": "6 CV", "7": "7 CV et plus" },
  moto:    { "2": "1 ou 2 CV", "5": "3 à 5 CV", "6": "Plus de 5 CV" },
  scooter: { "0": "≤ 50 cm³" },
};

function getTauxKm(typeVehicule, cvKey) {
  return BAREME_2024[typeVehicule]?.[cvKey]?.[0] || 0;
}

function calculerIndemnitesKm(km, typeVehicule, cvKey) {
  return (parseFloat(km) || 0) * getTauxKm(typeVehicule, cvKey);
}

export default function NewExpense() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [receiptPreviews, setReceiptPreviews] = useState({});
  const [receiptRotations, setReceiptRotations] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [typeVehicule, setTypeVehicule] = useState(user?.vehicle_type || "voiture");
  const [cvFiscal, setCvFiscal] = useState(user?.cv_fiscal || "5");
  const [date, setDate] = useState(new Date());

  const currentMonthDefault = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  };
  const [expenseMonth, setExpenseMonth] = useState(
    () => localStorage.getItem("noteFrais_expenseMonth") || currentMonthDefault()
  );

  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    currency: "EUR",
    category_id: "",
    vat_amount: "",
    vat_rate: "20",
    is_mileage: false,
    mileage_km: "",
    project: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (cameraOpen && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraOpen, cameraStream]);

  // Persister le mois de note de frais dans localStorage
  useEffect(() => {
    localStorage.setItem("noteFrais_expenseMonth", expenseMonth);
  }, [expenseMonth]);

  // Synchroniser le véhicule depuis le profil utilisateur quand user se charge
  useEffect(() => {
    if (user?.vehicle_type) setTypeVehicule(user.vehicle_type);
    if (user?.cv_fiscal) setCvFiscal(user.cv_fiscal);
  }, [user]);

  // Auto-activer frais kilométrique quand la catégorie correspondante est sélectionnée
  useEffect(() => {
    if (!formData.category_id || categories.length === 0) return;
    const cat = categories.find(c => c.id === formData.category_id);
    if (!cat) return;
    const isKm = cat.name.toLowerCase().includes("kilométr") || cat.name.toLowerCase().includes("kilometr");
    if (isKm && !formData.is_mileage) {
      setFormData(prev => ({ ...prev, is_mileage: true }));
    } else if (!isKm && formData.is_mileage) {
      setFormData(prev => ({ ...prev, is_mileage: false }));
    }
  }, [formData.category_id, categories]);

  // Recalculer le montant si le véhicule ou les CV changent
  useEffect(() => {
    if (formData.is_mileage && formData.mileage_km) {
      const km = parseFloat(formData.mileage_km) || 0;
      const montant = calculerIndemnitesKm(km, typeVehicule, cvFiscal);
      setFormData(prev => ({ ...prev, amount: montant.toFixed(2) }));
    }
  }, [typeVehicule, cvFiscal]);

  const openCamera = async (mode = facingMode) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      setCameraStream(stream);
      setFacingMode(mode);
      setCameraOpen(true);
    } catch (error) {
      if (error.name === "NotAllowedError") {
        toast.error("Accès à la caméra refusé. Autorisez l'accès dans les paramètres du navigateur.");
      } else if (error.name === "NotFoundError") {
        toast.error("Aucune caméra trouvée sur cet appareil.");
      } else {
        toast.error("Impossible d'accéder à la caméra.");
      }
    }
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);
  };

  const flipCamera = async () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    const newMode = facingMode === "environment" ? "user" : "environment";
    await openCamera(newMode);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);

    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);

    canvas.toBlob(async (blob) => {
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([blob], `justificatif_${timestamp}.jpg`, { type: "image/jpeg" });

      try {
        const localPreviewUrl = URL.createObjectURL(file);
        const response = await uploadReceipt(file);
        const receiptData = response.data;

        setReceiptPreviews(prev => ({
          ...prev,
          [receiptData.id]: { url: localPreviewUrl, type: file.type, name: file.name }
        }));
        setReceipts(prev => [...prev, receiptData]);
        toast.success("Photo ajoutée avec succès");

        // Ouvrir la preview pour vérifier l'orientation avant OCR
        setSelectedPreview({
          ...receiptData,
          previewUrl: localPreviewUrl,
          previewType: file.type
        });
        setZoomLevel(100);
        setPreviewOpen(true);
      } catch (error) {
        toast.error("Erreur lors du téléchargement de la photo");
      }
    }, "image/jpeg", 0.92);
  };

  const fetchData = async () => {
    try {
      const [categoriesRes, currenciesRes, policyRes] = await Promise.all([
        getCategories(),
        getCurrencies(),
        getPolicies()
      ]);
      setCategories(categoriesRes.data);
      setCurrencies(currenciesRes.data);
      setPolicy(policyRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate VAT when amount changes
    if (field === "amount" && formData.vat_rate) {
      const amount = parseFloat(value) || 0;
      const rate = parseFloat(formData.vat_rate) || 0;
      const vat = (amount * rate) / (100 + rate);
      setFormData(prev => ({ ...prev, vat_amount: vat.toFixed(2) }));
    }
    
    // Auto-calculer le montant kilométrique via le barème 2024
    if (field === "mileage_km" && formData.is_mileage) {
      const km = parseFloat(value) || 0;
      const montant = calculerIndemnitesKm(km, typeVehicule, cvFiscal);
      setFormData(prev => ({ ...prev, amount: montant.toFixed(2) }));
    }
  };

  const processFiles = async (files) => {
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 10MB)`);
        continue;
      }
      const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
      if (!allowed.includes(file.type)) {
        toast.error(`${file.name} : format non supporté`);
        continue;
      }
      try {
        const localPreviewUrl = URL.createObjectURL(file);
        const response = await uploadReceipt(file);
        const receiptData = response.data;
        setReceiptPreviews(prev => ({
          ...prev,
          [receiptData.id]: { url: localPreviewUrl, type: file.type, name: file.name }
        }));
        setReceipts(prev => [...prev, receiptData]);
        toast.success(`${file.name} téléchargé`);
        setSelectedPreview({ ...receiptData, previewUrl: localPreviewUrl, previewType: file.type });
        setZoomLevel(100);
        setPreviewOpen(true);
      } catch (error) {
        toast.error(`Erreur lors du téléchargement de ${file.name}`);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) await processFiles(files);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    await processFiles(files);
  };
  
  const openPreview = async (receipt) => {
    const preview = receiptPreviews[receipt.id];
    if (preview) {
      // Use local preview URL
      setSelectedPreview({
        ...receipt,
        previewUrl: preview.url,
        previewType: preview.type
      });
      setZoomLevel(100);
      setPreviewOpen(true);
    } else {
      // Load from server with authentication
      try {
        const response = await fetch(getReceiptImageUrl(receipt.id), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setSelectedPreview({
            ...receipt,
            previewUrl: blobUrl,
            previewType: receipt.content_type
          });
          setZoomLevel(100);
          setPreviewOpen(true);
        } else {
          toast.error("Impossible de charger l'aperçu");
        }
      } catch (error) {
        toast.error("Erreur lors du chargement de l'aperçu");
      }
    }
  };

  const handleOCR = async (receiptId) => {
    setOcrLoading(true);
    try {
      const rotation = receiptRotations[receiptId] || 0;
      const response = await processOCR(receiptId, rotation);
      const data = response.data;
      
      // Check for budget error
      if (data?.error_type === "budget_exceeded") {
        toast.error("Budget OCR épuisé. Veuillez recharger votre clé universelle dans Profil > Universal Key.");
        setOcrLoading(false);
        return;
      }
      
      // Check for format error
      if (data?.error_type === "unsupported_format") {
        toast.error("Format non supporté. Utilisez PNG, JPEG ou WebP.");
        setOcrLoading(false);
        return;
      }
      
      // Check for PDF conversion error
      if (data?.error_type === "pdf_conversion_failed") {
        toast.error("Impossible de convertir le PDF. Vérifiez que le fichier n'est pas corrompu.");
        setOcrLoading(false);
        return;
      }
      
      if (data && !data.parse_error) {
        // Auto-fill form with OCR data
        if (data.vendor) {
          setFormData(prev => ({ ...prev, description: data.vendor }));
        }
        if (data.total_amount) {
          setFormData(prev => ({ ...prev, amount: String(data.total_amount) }));
        }
        if (data.currency) {
          setFormData(prev => ({ ...prev, currency: data.currency }));
        }
        if (data.vat_amount) {
          setFormData(prev => ({ ...prev, vat_amount: String(data.vat_amount) }));
        }
        if (data.vat_rate) {
          setFormData(prev => ({ ...prev, vat_rate: String(data.vat_rate) }));
        }
        if (data.date) {
          setDate(new Date(data.date));
        }
        
        // Try to match category
        if (data.category_suggestion) {
          const categoryMap = {
            transport: "Transport",
            meals: "Repas",
            accommodation: "Hébergement",
            supplies: "Fournitures",
            other: "Autres"
          };
          const categoryName = categoryMap[data.category_suggestion] || data.category_suggestion;
          const matchedCategory = categories.find(c => 
            c.name.toLowerCase().includes(categoryName.toLowerCase())
          );
          if (matchedCategory) {
            setFormData(prev => ({ ...prev, category_id: matchedCategory.id }));
          }
        }
        
        toast.success("Données extraites avec succès");
      } else if (data?.error) {
        // Handle specific error types
        if (data.error_type === "budget_exceeded") {
          toast.error("Budget OCR épuisé. Rechargez dans Profil > Universal Key.");
        } else if (data.error_type === "unsupported_format") {
          toast.error("Format non supporté. Utilisez PNG, JPEG ou WebP.");
        } else {
          toast.error(data.message || "Erreur lors de l'analyse");
        }
      } else {
        toast.warning("Extraction partielle - vérifiez les données");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'analyse OCR";
      if (errorMsg.includes("Budget")) {
        toast.error("Budget OCR épuisé. Rechargez votre clé universelle.");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setOcrLoading(false);
    }
  };

  const removeReceipt = (receiptId) => {
    // Clean up local preview URL
    const preview = receiptPreviews[receiptId];
    if (preview?.url) {
      URL.revokeObjectURL(preview.url);
    }
    setReceiptPreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[receiptId];
      return newPreviews;
    });
    setReceipts(prev => prev.filter(r => r.id !== receiptId));
  };

  const handleSubmit = async (e, submitForApproval = false) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount || !formData.category_id) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setLoading(true);
    try {
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        vat_amount: parseFloat(formData.vat_amount) || 0,
        vat_rate: parseFloat(formData.vat_rate) || 20,
        mileage_km: formData.is_mileage ? parseFloat(formData.mileage_km) : null,
        mileage_rate: formData.is_mileage ? getTauxKm(typeVehicule, cvFiscal) : null,
        date: format(date, "yyyy-MM-dd"),
        expense_month: expenseMonth,
        receipt_ids: receipts.map(r => r.id)
      };
      
      const response = await createExpense(expenseData);

      if (submitForApproval) {
        const { submitExpense } = await import("../lib/api");
        await submitExpense(response.data.id);
        toast.success("Dépense soumise pour validation", {
          action: { label: "Voir les dépenses", onClick: () => navigate("/expenses") }
        });
      } else {
        toast.success("Dépense enregistrée en brouillon", {
          action: { label: "Voir les dépenses", onClick: () => navigate("/expenses") }
        });
      }

      // Réinitialiser le formulaire pour une nouvelle saisie
      setFormData({
        description: "",
        amount: "",
        currency: "EUR",
        category_id: "",
        vat_amount: "",
        vat_rate: "20",
        is_mileage: false,
        mileage_km: "",
        project: "",
        notes: ""
      });
      setDate(new Date());
      setReceipts([]);
      Object.values(receiptPreviews).forEach(p => p?.url && URL.revokeObjectURL(p.url));
      setReceiptPreviews({});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="new-expense-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="page-title">Nouvelle dépense</h1>
          <p className="page-description">Créez une nouvelle note de frais</p>
        </div>
      </div>

      <form onSubmit={(e) => handleSubmit(e, false)}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Receipt Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Justificatif
                </CardTitle>
                <CardDescription>
                  Téléchargez vos reçus pour extraction automatique (OCR)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${isDragging ? "border-primary bg-primary/5" : "hover:border-primary/50"}`}
                  onClick={() => document.getElementById('receipt-upload').click()}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Glissez vos fichiers ici ou cliquez pour sélectionner
                  </p>
                  <p className="text-xs text-muted-foreground">
                    JPEG, PNG, WebP ou PDF (max 10MB)
                  </p>
                  <input
                    id="receipt-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    data-testid="receipt-upload"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t" />
                  <span className="text-xs text-muted-foreground">ou</span>
                  <div className="flex-1 border-t" />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => openCamera()}
                  data-testid="camera-btn"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Prendre une photo
                </Button>
                <canvas ref={canvasRef} className="hidden" />
                
                {receipts.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {receipts.map((receipt) => {
                      const preview = receiptPreviews[receipt.id];
                      const isImage = receipt.content_type?.startsWith('image');
                      const isPdf = receipt.content_type === 'application/pdf';
                      
                      return (
                        <div 
                          key={receipt.id}
                          className="relative group border rounded-lg overflow-hidden bg-muted"
                        >
                          {/* Thumbnail Preview */}
                          <div
                            className="aspect-[4/3] relative cursor-pointer"
                            onClick={() => openPreview(receipt)}
                          >
                            {preview && isImage ? (
                              <img
                                src={preview.url}
                                alt={receipt.filename}
                                className="w-full h-full object-cover transition-transform duration-300"
                                style={{ transform: `rotate(${receiptRotations[receipt.id] || 0}deg)` }}
                              />
                            ) : isPdf ? (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
                                <FileText className="h-12 w-12 text-red-500 mb-2" />
                                <span className="text-xs text-red-600 font-medium">PDF</span>
                              </div>
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center">
                                <FileImage className="h-12 w-12 text-muted-foreground mb-2" />
                                <span className="text-xs text-muted-foreground">Aperçu</span>
                              </div>
                            )}
                            
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Eye className="h-8 w-8 text-white" />
                            </div>
                          </div>
                          
                          {/* File info and actions */}
                          <div className="p-3">
                            <p className="text-sm font-medium truncate mb-2" title={receipt.filename}>
                              {receipt.filename}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                title="Pivoter de 90°"
                                onClick={() => setReceiptRotations(prev => ({
                                  ...prev,
                                  [receipt.id]: ((prev[receipt.id] || 0) + 90) % 360
                                }))}
                              >
                                <RotateCw className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleOCR(receipt.id)}
                                disabled={ocrLoading}
                                data-testid={`ocr-btn-${receipt.id}`}
                              >
                                {ocrLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <Sparkles className="h-4 w-4 mr-1" />
                                    OCR
                                  </>
                                )}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => openPreview(receipt)}
                                data-testid={`preview-btn-${receipt.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => removeReceipt(receipt.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expense Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Détails de la dépense</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Mois de la note de frais</Label>
                  <div className="flex gap-2">
                    <Select
                      value={expenseMonth ? expenseMonth.split("-")[1] : ""}
                      onValueChange={(m) => {
                        const y = expenseMonth ? expenseMonth.split("-")[0] : String(new Date().getFullYear());
                        setExpenseMonth(`${y}-${m}`);
                      }}
                    >
                      <SelectTrigger className="flex-1" data-testid="expense-month-month">
                        <SelectValue placeholder="Mois" />
                      </SelectTrigger>
                      <SelectContent>
                        {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"].map((nom, i) => (
                          <SelectItem key={i+1} value={String(i+1).padStart(2,"0")}>{nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={expenseMonth ? expenseMonth.split("-")[0] : ""}
                      onValueChange={(y) => {
                        const m = expenseMonth ? expenseMonth.split("-")[1] : String(new Date().getMonth()+1).padStart(2,"0");
                        setExpenseMonth(`${y}-${m}`);
                      }}
                    >
                      <SelectTrigger className="w-28" data-testid="expense-month-year">
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    placeholder="Ex: Déjeuner client"
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    data-testid="expense-description"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Catégorie *</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={(value) => handleInputChange("category_id", value)}
                    >
                      <SelectTrigger data-testid="expense-category">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !date && "text-muted-foreground"
                          )}
                          data-testid="expense-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP", { locale: fr }) : "Sélectionner"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Mileage Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <Label htmlFor="mileage-toggle" className="font-medium">Frais kilométrique</Label>
                    <p className="text-sm text-muted-foreground">
                      Barème fiscal 2024 (DGFIP)
                    </p>
                  </div>
                  <Switch
                    id="mileage-toggle"
                    checked={formData.is_mileage}
                    onCheckedChange={(checked) => handleInputChange("is_mileage", checked)}
                    data-testid="mileage-toggle"
                  />
                </div>

                {formData.is_mileage ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Type de véhicule</Label>
                        <Select
                          value={typeVehicule}
                          onValueChange={(v) => {
                            setTypeVehicule(v);
                            setCvFiscal(v === "voiture" ? "5" : v === "moto" ? "5" : "0");
                          }}
                        >
                          <SelectTrigger data-testid="vehicle-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="voiture">Voiture</SelectItem>
                            <SelectItem value="moto">Moto (&gt; 50 cm³)</SelectItem>
                            <SelectItem value="scooter">Scooter (≤ 50 cm³)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {typeVehicule !== "scooter" && (
                        <div className="space-y-2">
                          <Label>Puissance fiscale</Label>
                          <Select value={cvFiscal} onValueChange={setCvFiscal} data-testid="cv-fiscal">
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(CV_LABELS[typeVehicule]).map(([key, label]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mileage_km">Distance (km)</Label>
                      <Input
                        id="mileage_km"
                        type="number"
                        step="0.1"
                        placeholder="Ex: 150"
                        value={formData.mileage_km}
                        onChange={(e) => handleInputChange("mileage_km", e.target.value)}
                        data-testid="mileage-km"
                      />
                      {formData.mileage_km && (() => {
                        const km = parseFloat(formData.mileage_km) || 0;
                        const taux = getTauxKm(typeVehicule, cvFiscal);
                        const montant = (km * taux).toFixed(2);
                        return (
                          <p className="text-sm text-muted-foreground">
                            {km} km × {taux.toFixed(3).replace(".", ",")} €/km
                            {" = "}
                            <span className="font-semibold text-foreground">{montant} €</span>
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="amount">Montant TTC *</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formData.amount}
                        onChange={(e) => handleInputChange("amount", e.target.value)}
                        data-testid="expense-amount"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="vat_rate">Taux TVA (%)</Label>
                      <Select
                        value={formData.vat_rate}
                        onValueChange={(value) => {
                          handleInputChange("vat_rate", value);
                          const amount = parseFloat(formData.amount) || 0;
                          const rate = parseFloat(value) || 0;
                          const vat = (amount * rate) / (100 + rate);
                          setFormData(prev => ({ ...prev, vat_amount: vat.toFixed(2) }));
                        }}
                      >
                        <SelectTrigger data-testid="expense-vat-rate">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0% — Exonéré</SelectItem>
                          <SelectItem value="2.1">2,1% — Super réduit</SelectItem>
                          <SelectItem value="5.5">5,5% — Réduit</SelectItem>
                          <SelectItem value="10">10% — Intermédiaire</SelectItem>
                          <SelectItem value="20">20% — Normal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Devise — toujours visible */}
                <div className="space-y-2">
                  <Label htmlFor="currency">Devise *</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => handleInputChange("currency", value)}
                    required
                  >
                    <SelectTrigger id="currency" data-testid="expense-currency">
                      <SelectValue placeholder="Sélectionner une devise" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((curr) => (
                        <SelectItem key={curr.code} value={curr.code}>
                          {curr.code} — {curr.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {!formData.is_mileage && formData.vat_amount && (
                  <div className="space-y-2">
                    <Label htmlFor="vat_amount">Montant TVA</Label>
                    <Input
                      id="vat_amount"
                      type="number"
                      step="0.01"
                      value={formData.vat_amount}
                      onChange={(e) => handleInputChange("vat_amount", e.target.value)}
                      data-testid="expense-vat-amount"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="project">Projet (optionnel)</Label>
                  <Input
                    id="project"
                    placeholder="Ex: Projet Alpha"
                    value={formData.project}
                    onChange={(e) => handleInputChange("project", e.target.value)}
                    data-testid="expense-project"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optionnel)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Informations complémentaires..."
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    rows={3}
                    data-testid="expense-notes"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg">Résumé</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Montant HT</span>
                    <span className="tabular-nums">
                      {formData.amount && formData.vat_amount
                        ? (parseFloat(formData.amount) - parseFloat(formData.vat_amount)).toFixed(2)
                        : "0.00"} {formData.currency}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">TVA</span>
                    <span className="tabular-nums">
                      {formData.vat_amount || "0.00"} {formData.currency}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Total TTC</span>
                    <span className="tabular-nums text-lg">
                      {formData.amount || "0.00"} {formData.currency}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 pt-4">
                  <Button
                    type="submit"
                    variant="outline"
                    className="w-full"
                    disabled={loading}
                    data-testid="save-draft-btn"
                  >
                    Enregistrer en brouillon
                  </Button>
                  <Button
                    type="button"
                    className="w-full"
                    disabled={loading}
                    onClick={(e) => handleSubmit(e, true)}
                    data-testid="submit-btn"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Soumettre pour validation
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* Camera Dialog */}
      <Dialog open={cameraOpen} onOpenChange={(open) => { if (!open) closeCamera(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Prendre une photo du justificatif</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={flipCamera}
                title="Retourner la caméra"
              >
                <FlipHorizontal className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={closeCamera}
              >
                Annuler
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={capturePhoto}
              >
                <Camera className="h-4 w-4 mr-2" />
                Capturer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate pr-4">{selectedPreview?.filename}</span>
              <div className="flex items-center gap-2">
                {/* Rotation — images seulement */}
                {selectedPreview?.previewType !== 'application/pdf' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      title="Pivoter de 90°"
                      onClick={() => {
                        if (!selectedPreview) return;
                        setReceiptRotations(prev => ({
                          ...prev,
                          [selectedPreview.id]: ((prev[selectedPreview.id] || 0) + 90) % 360
                        }));
                      }}
                    >
                      <RotateCw className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground w-8 text-center">
                      {receiptRotations[selectedPreview?.id] || 0}°
                    </span>
                    <span className="w-px h-4 bg-border" />
                  </>
                )}
                {/* Zoom */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZoomLevel(prev => Math.max(25, prev - 25))}
                  disabled={zoomLevel <= 25}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-12 text-center">{zoomLevel}%</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZoomLevel(prev => Math.min(200, prev + 25))}
                  disabled={zoomLevel >= 200}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto bg-muted rounded-lg p-4 min-h-[400px]">
            {selectedPreview && (
              selectedPreview.previewType === 'application/pdf' ? (
                <iframe
                  src={selectedPreview.previewUrl}
                  className="w-full h-full min-h-[500px] rounded border"
                  title={selectedPreview.filename}
                />
              ) : (
                <div className="flex items-center justify-center min-h-full">
                  <img
                    src={selectedPreview.previewUrl}
                    alt={selectedPreview.filename}
                    className="max-w-full h-auto transition-transform duration-300"
                    style={{
                      transform: `scale(${zoomLevel / 100}) rotate(${receiptRotations[selectedPreview.id] || 0}deg)`
                    }}
                  />
                </div>
              )
            )}
          </div>

          {/* Pied de dialog : lancer l'OCR directement */}
          {selectedPreview && (
            <div className="flex justify-end pt-2 border-t">
              <Button
                size="sm"
                className="gap-2"
                onClick={() => {
                  setPreviewOpen(false);
                  handleOCR(selectedPreview.id);
                }}
                disabled={ocrLoading}
              >
                {ocrLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Lancer l'OCR
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
